import React, { useState } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const inquiryOptions = [
    { value: 'booking', label: 'Booking Inquiry' },
    { value: 'general', label: 'General Information' },
    { value: 'activities', label: 'Activities & Experiences' },
    { value: 'events', label: 'Events & Group Bookings' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'support', label: 'Customer Support' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      inquiryType: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Fixed typo: VITE_EMAILJS_PUBLIC_KEY instead of VITE_EMAIL_PUBLIC_KEY
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '71BeoJTGpqp30CQtP';
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_64ckxkx';
    const contactTemplateId = import.meta.env.VITE_EMAILJS_CONTACT_TEMPLATE_ID || 'template_z6b8u1b';
    const bookingTemplateId = import.meta.env.VITE_EMAILJS_BOOKING_TEMPLATE_ID || 'template_th6b7dk';

    try {
      // 1. Send notification to ADMIN
      try {
        console.log('Sending Admin Contact Notification...');
        await emailjs.send(
          serviceId,
          contactTemplateId,
          {
            name: formData.name,
            email: formData.email,
            message: formData.message,
            inquiry_type: formData.inquiryType
          },
          publicKey
        );
        console.log('Admin notification sent.');
      } catch (adminErr) {
        console.error('Failed to send Admin notification:', adminErr);
      }

      // 2. Send "Thank You" reply to USER
      // Using bookingTemplateId as it supports dynamic recipients
      try {
        console.log('Sending User Thank You to:', formData.email);
        await emailjs.send(
          serviceId,
          bookingTemplateId,
          {
            // Redundant email fields to ensure template mapping works (Matches PaymentSection logic)
            to_email: formData.email,
            email: formData.email,

            subject: "🌿 Thank you for contacting Dandeli Adventure Resorts",
            guest_name: formData.name,
            guest_email: formData.email,
            phone: formData.phone,
            checkin: "N/A",
            checkout: "N/A",
            guests: "N/A",
            room: "Contact Inquiry",
            note: `We received your message: "${formData.message}"`
          },
          publicKey
        );
        console.log('User thank you sent.');
      } catch (userErr) {
        console.error('Failed to send User thank you:', userErr);
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: ''
      });

      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Overall ContactForm Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-organic p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="MessageSquare" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-card-foreground">
            Send us a Message
          </h2>
          <p className="text-sm text-muted-foreground">
            We'll get back to you within 24 hours
          </p>
        </div>
      </div>
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <p className="text-sm text-success font-medium">
              Thank you! Your message has been sent successfully.
            </p>
          </div>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <p className="text-sm text-error font-medium">
              Something went wrong. Please check the console for details.
            </p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={handleInputChange}
            required
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="+91 98765 43210"
            value={formData?.phone}
            onChange={handleInputChange}
            required
          />

          <Select
            label="Inquiry Type"
            placeholder="Select inquiry type"
            options={inquiryOptions}
            value={formData?.inquiryType}
            onChange={handleSelectChange}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            Message
          </label>
          <textarea
            name="message"
            rows={5}
            placeholder="Tell us how we can help you..."
            value={formData?.message}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
          />
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          disabled={!formData?.name || !formData?.email || !formData?.phone || !formData?.inquiryType || !formData?.message}
        >
          {isSubmitting ? 'Sending Message...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;